import { useEffect, useState } from "react";
import { useEditorStore } from "../../store/editor.js";

/**
 * Why the last touch on the board did nothing — "No road left", "Towers go
 * beside the road" — shown for a moment over the board.
 *
 * Friends test, Sep 2026: going past a limit used to be ALLOWED and then
 * reported as an error below the board. Now the board refuses, and this says
 * why, where the eyes already are. A short buzz on phones that support it.
 */
export function BoardNotice(): JSX.Element | null {
  const notice = useEditorStore((s) => s.notice);
  const [shown, setShown] = useState<number | null>(null);

  useEffect(() => {
    if (!notice) return;
    setShown(notice.seq);
    try {
      navigator.vibrate?.(35);
    } catch {
      // Not every browser allows it; the text is the message.
    }
    const timer = setTimeout(() => {
      setShown((current) => (current === notice.seq ? null : current));
      if (useEditorStore.getState().notice?.seq === notice.seq) useEditorStore.getState().clearNotice();
    }, 2400);
    return () => clearTimeout(timer);
  }, [notice]);

  if (!notice || shown !== notice.seq) return null;
  return (
    <div className="pointer-events-none absolute inset-x-3 top-14 z-20 flex justify-center">
      <div
        role="status"
        data-testid="board-notice"
        className="max-w-sm rounded-lg border border-warn/60 bg-bg/95 px-3 py-2 text-center text-sm font-semibold text-ink shadow-lg"
      >
        {notice.text}
      </div>
    </div>
  );
}
